// ============================================================
// THE DIFFICULTY NOTCH - how hard the next test on the drum is.
//
// The drum's opposed actions (Knock-Down, Grapple, Feint, Stun,
// Manoeuvre, and Stealth's quiet work) took no modifier: the rulebook
// leaves "how hard is it here" to the GM, and there was nowhere to say
// it. The notch is that place. It holds a modifier for the one acting
// (`mine`) and, for whoever directs, one for the other side (`theirs`).
// It applies to the NEXT action that is a test and then returns to rest.
//
// Pure. Zero React dependency.
// ============================================================
import { DIFFICULTY_LADDER } from '../characteristicTests';

export interface Notch {
  /** The modifier on the acting side's test. */
  mine: number;
  /** The modifier on the other side's test - the director's to set. */
  theirs: number;
}

/** Challenging +0 on both sides: the notch at rest changes nothing. */
export const NOTCH_AT_REST: Notch = Object.freeze({ mine: 0, theirs: 0 });

export const isAtRest = (notch: Notch): boolean => notch.mine === 0 && notch.theirs === 0;

const signed = (mod: number): string => (mod >= 0 ? `+${mod}` : String(mod));

/** The steps the notch offers: the whole ladder, easiest first, each with its label. */
export const NOTCH_STEPS: readonly { mod: number; label: string }[] = DIFFICULTY_LADDER.map((step) => ({
  mod: step.mod,
  label: `${step.label} ${signed(step.mod)}`,
}));

const LIMIT = 60;

/** A modifier held to the ladder's ends; anything that is not a number is +0. */
export function clampMod(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : 0;
  return Math.max(-LIMIT, Math.min(LIMIT, n));
}

/** The notch with one side changed. `theirs` is the director's: anyone else's change to it is ignored. */
export function turnNotch(notch: Notch, side: keyof Notch, mod: unknown, canSetTheirs: boolean): Notch {
  if (side === 'theirs' && !canSetTheirs) return notch;
  return { ...notch, [side]: clampMod(mod) };
}
